import React from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays } from 'date-fns';
import * as S from './Statistics.styles';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdStatus } from '@/types';
import { useAdDetails } from '@/hooks/useAdDetails';
import { useServerDataContext } from '@/contexts';
import NoSsr from '@/components/NoSsr/NoSsr';
import { getMessageTime } from '@/utils/datetime.utils';
import { setSuffix } from '@/utils/helpers';

export const Statistics = () => {
  const { t, i18n } = useTranslation();
  const { adDetails } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const {
    legacyResourceId,
    status,
    hits,
    expiresAt,
    callsCount,
    bookmarksCount,
    updatedAt,
  } = adData ?? {};

  const daysToDeactivation =
    expiresAt && differenceInCalendarDays(new Date(expiresAt), new Date());

  return (
    <S.Container>
      <S.Row data-testid={AdPageTestIds.statistics.adNumber}>
        <S.Label>{t('pages.ad.ad_number')}:</S.Label>
        <S.Value>{legacyResourceId}</S.Value>
      </S.Row>
      {typeof daysToDeactivation === 'number' &&
        status !== AdStatus.Expired && (
          <S.Row data-testid={AdPageTestIds.statistics.daysToDeactivation}>
            <S.Label>{t('pages.ad.days_to_deactivation')}:</S.Label>
            <S.Value>
              {t(
                setSuffix(i18n, daysToDeactivation.toString(), 'common.n_days'),
                {
                  days: daysToDeactivation,
                }
              )}
            </S.Value>
          </S.Row>
        )}
      {updatedAt && (
        <S.Row data-testid={AdPageTestIds.statistics.updatedAt}>
          <S.Label>{t('pages.ad.updated')}:</S.Label>
          <NoSsr>
            <S.Value>{getMessageTime(new Date(updatedAt), i18n, true)}</S.Value>
          </NoSsr>
        </S.Row>
      )}

      <S.Divider />

      <S.Row data-testid={AdPageTestIds.statistics.views}>
        <S.Label>{t('pages.ad.views')}</S.Label>
        <S.Value>{hits ?? 0}</S.Value>
      </S.Row>

      <S.Row data-testid={AdPageTestIds.statistics.contacts.self}>
        <S.Label>
          {t('pages.ad.contacts')}
          <NoSsr
            fallback={
              <S.QuestionIcon
                data-testid={AdPageTestIds.statistics.contacts.questionIcon}
              />
            }
          >
            <S.Tooltip
              offset={35}
              allowedTriggers={['hover']}
              placement="top"
              dataTestId={AdPageTestIds.statistics.contacts.tooltip}
              content={t('pages.ad.contacts_tip')}
            >
              <S.QuestionIcon
                data-testid={AdPageTestIds.statistics.contacts.questionIcon}
              />
            </S.Tooltip>
          </NoSsr>
        </S.Label>
        <S.Value>{callsCount ?? 0}</S.Value>
      </S.Row>

      <S.Row data-testid={AdPageTestIds.statistics.inFavorites}>
        <S.Label>{t('pages.ad.in_favorites')}</S.Label>
        <S.Value>{bookmarksCount ?? 0}</S.Value>
      </S.Row>
    </S.Container>
  );
};
